import { SinglePath } from '@hwm/ui-lib';
import { AppFeatureFlags, AppPermissions } from '@root/appPermissions';

// constants

export const accountAppPath = 'account';
export const createDmaAppPath = `${accountAppPath}/createDma`;
export const siteBulkActionsAppPath = 'bulk';
export const correlationAppPath = 'correlation';

// interfaces & types
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type ExtractParams<T extends string> = T extends `${infer _Start}:${infer Param}/${infer Rest}`
  ? { [K in Param | keyof ExtractParams<Rest>]: string }
  : // eslint-disable-next-line @typescript-eslint/no-unused-vars
    T extends `${infer _Start}:${infer Param}`
    ? { [K in Param]: string }
    : {};

interface SiteAccountPathsArgs<BasePathType extends string> {
  basePath: BasePathType;
  featureFlag?: string;
  permissions?: AppPermissions[];
}

// site paths factory

export const accountPathsFactory = <BasePathType extends string>({
  basePath,
  featureFlag,
  permissions,
}: SiteAccountPathsArgs<BasePathType>) => {
  const paths = {
    withAccountSider: {
      path: `/${basePath}/${accountAppPath}`,
    },
    withCreateDmaSider: {
      path: `/${basePath}/${createDmaAppPath}`,
    },
    //
    base: { path: `/${basePath}` },
  } as const;

  return addPermissionsAndFeatureFlagsToPaths(paths, permissions, featureFlag);
};

export const siteAndAccountPathsFactory = <BasePathType extends string>({
  basePath,
  featureFlag,
  permissions,
}: SiteAccountPathsArgs<BasePathType>) => {
  const paths = {
    ...accountPathsFactory({ basePath, featureFlag, permissions }),
    withAuditList: createParametrizedPath(basePath, ':id/history', (params) => `/${basePath}/${params.id}/history`),
    withNoteList: createParametrizedPath(basePath, ':id/notes', (params) => `/${basePath}/${params.id}/notes`),
    withHistogram: createParametrizedPath(basePath, ':id/histogram', (params) => `/${basePath}/${params.id}/histogram`),
    withLoggerProgramming: createParametrizedPath(
      basePath,
      ':id/program/:loggerId',
      (params) => `/${basePath}/${params.id}/program/${params.loggerId}`,
      [AppPermissions.CanEditSites]
    ),
    withGallery: createParametrizedPath(basePath, ':id/gallery', (params) => `/${basePath}/${params.id}/gallery`),
    details: createParametrizedPath(basePath, ':id', (params) => `/${basePath}/${params.id}`),
    detailsEdit: createParametrizedPath(basePath, ':id/edit', (params) => `/${basePath}/${params.id}/edit`),
    withCorrelationList: createParametrizedPath(
      basePath,
      ':id/correlationList',
      (params) => `/${basePath}/${params.id}/correlationList`
    ),
    withLnS: createParametrizedPath(basePath, ':id/lns', (params) => `/${basePath}/${params.id}/lns`),
    withCsq: createParametrizedPath(basePath, ':id/csq', (params) => `/${basePath}/${params.id}/csq`),
    withSignal: createParametrizedPath(basePath, ':id/signal', (params) => `/${basePath}/${params.id}/signal`),
    withAudio: createParametrizedPath(basePath, ':id/audio', (params) => `/${basePath}/${params.id}/audio`),
    withSingleAudio: createParametrizedPath(
      basePath,
      ':id/audio/:recordingId',
      (params) => `/${basePath}/${params.id}/audio/${params.recordingId}`
    ),
    //
    base: { path: `/${basePath}` },
  } as const;

  return addPermissionsAndFeatureFlagsToPaths(paths, permissions, featureFlag);
};

// path builder helpers

export const createParametrizedPath = <BasePathType extends string, PathTemplateType extends string>(
  basePath: BasePathType,
  pathTemplate: PathTemplateType,
  getLink?: (params: ExtractParams<BasePathType> & ExtractParams<PathTemplateType>) => string,
  //
  permissions: AppPermissions[] = null,
  featureFlag: string = null
) => {
  return {
    pathWithParam: `/${basePath}/${pathTemplate}`,
    getLink: (params: ExtractParams<BasePathType> & ExtractParams<PathTemplateType>) =>
      getLink?.(params) ?? `/${basePath}/${pathTemplate}`,
    permissions,
    featureFlag,
  } as const;
};

// helpers

const addPermissionsAndFeatureFlagsToPaths = <Paths extends { [x: string]: SinglePath }>(
  paths: Paths,
  permissions: AppPermissions[] = null,
  featureFlag: string = null
) => {
  const pathsCopy = { ...paths };

  Object.keys(paths).forEach((pathKey) => {
    const path = pathsCopy[pathKey];

    if (typeof path !== 'string') {
      if (permissions?.length) {
        path.permissions = [...(path.permissions ?? []), ...permissions];
      }
      if (featureFlag) {
        path.featureFlag = featureFlag ?? null;
      }
    }
  });

  return pathsCopy;
};

// additional paths

export const FunctionsPathsOptions = {
  uploadSiteNotes: {
    path: '/functions/uploadSiteNotes' as const,
    permissions: [AppPermissions.CanEditSites],
    featureFlag: AppFeatureFlags.SiteNoteBulkUpload,
  },
  linkedPMZones: {
    path: '/functions/linkedPMZones' as const,
    permissions: [AppPermissions.CanLinkAccounts],
  },
  bulkActions: {
    path: '/functions/bulkActions' as const,
    permissions: [AppPermissions.CanUseFunctionsBulkActions],
  },
};

export const CorrelationPathsOptions = {
  details: createParametrizedPath(
    `${correlationAppPath}`,
    ':correlationId/:id',
    (params) => `/${correlationAppPath}/${params.correlationId}/${params.id}`
  ),
  detailsEdit: createParametrizedPath(
    `${correlationAppPath}`,
    ':correlationId/:id/edit',
    (params) => `/${correlationAppPath}/${params.correlationId}/${params.id}/edit`
  ),
  base: createParametrizedPath(
    `${correlationAppPath}`,
    ':correlationId',
    ({ correlationId }) => `/${correlationAppPath}/${correlationId}`
  ),
};

export const AudioListPathsOptions = {
  sitesMap: createParametrizedPath('sitesMap', ':id/audioList', ({ id }) => `/sitesMap/${id}/audioList`, [
    AppPermissions.CanViewDeviceRecordings,
  ]),
  sites: createParametrizedPath('sites', ':id/audioList', ({ id }) => `/sites/${id}/audioList`, [
    AppPermissions.CanViewDeviceRecordings,
  ]),
  lns: createParametrizedPath('lns', ':id/audioList', ({ id }) => `/lns/${id}/audioList`, [
    AppPermissions.CanViewDeviceRecordings,
  ]),
  newLeaks: createParametrizedPath(
    'newLeaks',
    ':id/audioList',
    ({ id }) => `/newLeaks/${id}/audioList`,
    [AppPermissions.CanViewDeviceRecordings],
    AppFeatureFlags.NewLeaks
  ),
  leakAnalysis: createParametrizedPath(
    'leakAnalysis',
    ':id/audioList',
    ({ id }) => `/leakAnalysis/${id}/audioList`,
    [AppPermissions.CanViewDeviceRecordings],
    AppFeatureFlags.LeakAnalysis
  ),
  maintenance: createParametrizedPath('maintenance', ':id/audioList', ({ id }) => `/maintenance/${id}/audioList`, [
    AppPermissions.CanViewDeviceRecordings,
  ]),
} as const;

export const AudioAnalysisDetailsPathsOptions = {
  base: createParametrizedPath(
    `audioAnalysis/details`,
    ':requestId',
    ({ requestId }) => `/audioAnalysis/details/${requestId}`,
    null,
    AppFeatureFlags.MLAudioAnalysis
  ),
  withAudioAnalysisDetails: createParametrizedPath(
    'audioAnalysis/details',
    ':requestId/:audioAnalysisId',
    ({ requestId, audioAnalysisId }) => `/audioAnalysis/details/${requestId}/${audioAnalysisId}`,
    null,
    AppFeatureFlags.MLAudioAnalysis
  ),
};
